// Inventory API client. See api/src/routes/inventory.ts.
//
// Stock pivot: rows = (item × brand × style × color), one column per size,
// cells per store. Multi-store native — `byStore[size][storeDb] = qty`.

export interface StockRow {
  /** Owning store when several are selected; null in single-store mode. */
  storeDb: string | null;
  item: string;
  brand: string;
  style: string;
  color: string;
  subgroup: string;
  mrp: number;
  sp: number;
  byStore: Record<string, Record<string, number>>;
  total: number;
}

export interface StockResponse {
  stores: string[];
  sizes: string[];
  rows: StockRow[];
  totalRows: number;
  returnedRows?: number;
  truncated?: boolean;
  cache?: { source: 'cache' | 'live'; states: Array<{ storeDb: string; refreshedAt: string | null; rowCount: number; ageMs: number | null }> };
}

export type MatchMode = 'contains' | 'startswith' | 'similar';

/**
 * SIZEGRPCODE from the POS size-group master (e.g. 'G00001'). Previously a
 * hardcoded union — staff maintain the real list in the POS, so it now comes
 * from /inventory/masters.
 */
export type SizeGroup = string;

export interface SizeGroupOption {
  code: string;
  label: string;
  sizes: string[];
}

export interface StockFilters {
  brand?: string | null;
  style?: string | null;
  color?: string | null;
  subgroup?: string | null;
  item?: string | null;
  vendor?: string | null;
  q?: string | null;
  match?: MatchMode;
  sizeGroup?: SizeGroup | null;
  stores?: string[] | null;
  limit?: number;
}

export interface MastersResponse {
  brand: string[];
  style: string[];
  color: string[];
  subgroup: string[];
  item: string[];
  sizeGroups: SizeGroupOption[];
}

export interface ItemDetail {
  item: string; brand: string; style: string; color: string;
  subgroup: string;
  perStore: Array<{
    storeDb: string;
    stickers: Array<{ size: string; barcode: string; purchasedOn: string; mrp: number; sp: number }>;
  }>;
  imageUrls: string[];
}

function qs(p: Record<string, string | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '') u.set(k, v);
  return u.toString();
}

export async function getStock(f: StockFilters): Promise<StockResponse> {
  const r = await fetch(`/api/inventory/stock?${qs({
    brand: f.brand ?? undefined,
    style: f.style ?? undefined,
    color: f.color ?? undefined,
    subgroup: f.subgroup ?? undefined,
    item: f.item ?? undefined,
    vendor: f.vendor ?? undefined,
    q: f.q ?? undefined,
    stores: f.stores?.join(',') ?? undefined,
    limit: f.limit ? String(f.limit) : undefined,
    match: f.match && f.match !== 'contains' ? f.match : undefined,
    sizeGroup: f.sizeGroup ?? undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`stock failed (${r.status})`);
  return r.json() as Promise<StockResponse>;
}

export async function getMasters(): Promise<MastersResponse> {
  const r = await fetch('/api/inventory/masters', { credentials: 'include' });
  if (!r.ok) throw new Error(`masters failed (${r.status})`);
  return r.json() as Promise<MastersResponse>;
}

// ─── transfers ──────────────────────────────────────────────────────────

export type TransferDirection = 'all' | 'in' | 'out';

export interface TransferRow {
  billNo: string; billDate: string;
  src: string; dst: string;
  direction: 'in' | 'out' | 'other';
  transferTime: string; posUser: string;
  barcode: string; rowrollNo: string;
  itemName: string;
  brand: string; style: string; color: string;
  size: string; subgroup: string;
  qty: number; mrp: number;
}

export interface TransferTotals {
  outCount: number; inCount: number;
  outQty: number;   inQty: number;
  outAmt: number;   inAmt: number;
}

export interface TransferResponse {
  store: string;
  /** Auto-detected primary sub-store for this DB ("home"). */
  home: string;
  page: number; limit: number; total: number;
  rows: TransferRow[];
  totals: TransferTotals;
}

export interface TransferFilters {
  from: string; to: string;
  direction?: TransferDirection;
  style?: string | null;
  color?: string | null;
  subgroup?: string | null;
  item?: string | null;
  q?: string | null;
  store?: string | null;
  page?: number;
  limit?: number;
}

export async function getTransfers(f: TransferFilters): Promise<TransferResponse> {
  const r = await fetch(`/api/inventory/transfers?${qs({
    from: f.from, to: f.to,
    direction: f.direction && f.direction !== 'all' ? f.direction : undefined,
    style:    f.style ?? undefined,
    color:    f.color ?? undefined,
    subgroup: f.subgroup ?? undefined,
    item:     f.item ?? undefined,
    q:        f.q ?? undefined,
    store:    f.store ?? undefined,
    page:     f.page ? String(f.page) : undefined,
    limit:    f.limit ? String(f.limit) : undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`transfers failed (${r.status})`);
  return r.json() as Promise<TransferResponse>;
}

export async function getItemDetail(p: {
  item: string; brand: string; style: string; color: string;
  store?: string;
}): Promise<ItemDetail> {
  const r = await fetch(`/api/inventory/item/detail?${qs({
    item: p.item, brand: p.brand, style: p.style, color: p.color,
    ...(p.store ? { store: p.store } : {}),
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`detail failed (${r.status})`);
  return r.json() as Promise<ItemDetail>;
}

// ─── movement analysis ──────────────────────────────────────────────────────

export type MovementCategory = 'all' | 'fast' | 'slow' | 'dead' | 'new';

export interface MovementRow {
  storeDb: string;
  storeName: string;
  item: string;
  brand: string;
  style: string;
  color: string;
  subgroup: string;
  mrp: number;
  sp: number;
  stockQty: number;
  lastSaleDate: number | null;
  daysSinceLastSale: number | null;
  totalSoldQty: number;
  category: 'fast' | 'slow' | 'dead' | 'new';
}

export interface MovementSummary {
  fastCount: number;
  fastValue: number;
  slowCount: number;
  slowValue: number;
  deadCount: number;
  deadValue: number;
  newCount: number;
  newValue: number;
  totalStock: number;
  totalValue: number;
}

export interface MovementResponse {
  store: string;
  fastDays: number;
  deadDays: number;
  category: MovementCategory;
  summary: MovementSummary;
  total: number;
  page: number;
  limit: number;
  rows: MovementRow[];
}

export interface MovementFilters {
  store: string;
  fastDays?: number;
  deadDays?: number;
  category?: MovementCategory;
  subgroup?: string | null;
  brand?: string | null;
  /** Article / style code. */
  style?: string | null;
  item?: string | null;
  color?: string | null;
  /** Multi-field search across brand, article, item and colour. */
  q?: string | null;
  match?: MatchMode;
  page?: number;
  limit?: number;
}

export async function getMovement(f: MovementFilters): Promise<MovementResponse> {
  const r = await fetch(`/api/inventory/movement?${qs({
    store: f.store,
    fastDays: f.fastDays ? String(f.fastDays) : undefined,
    deadDays: f.deadDays ? String(f.deadDays) : undefined,
    category: f.category && f.category !== 'all' ? f.category : undefined,
    subgroup: f.subgroup ?? undefined,
    brand: f.brand ?? undefined,
    style: f.style ?? undefined,
    item: f.item ?? undefined,
    color: f.color ?? undefined,
    q: f.q ?? undefined,
    match: f.match && f.match !== 'contains' ? f.match : undefined,
    page: f.page ? String(f.page) : undefined,
    limit: f.limit ? String(f.limit) : undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`movement failed (${r.status})`);
  return r.json() as Promise<MovementResponse>;
}

// ─── stock breakdown ────────────────────────────────────────────────────────

export interface StockTransaction {
  source: string;
  direction: 'in' | 'out';
  size: string;
  barcode: string;
  qty: number;
  /** Bill time, when the source header records one. */
  time?: string | null;
  date: string;
  billNo: string;
  counterpartStore: string;
}

export interface StockBreakdownResponse {
  item: string;
  brand: string;
  style: string;
  color: string;
  size: string | null;
  perStore: Array<{
    storeDb: string;
    transactions: StockTransaction[];
  }>;
  summary: {
    totalIn: number;
    totalOut: number;
    netStock: number;
  };
}

export async function getStockBreakdown(p: {
  item: string; brand: string; style: string; color: string; store?: string; size?: string;
}): Promise<StockBreakdownResponse> {
  const r = await fetch(`/api/inventory/item/breakdown?${qs({
    item: p.item, brand: p.brand, style: p.style, color: p.color,
    store: p.store ?? undefined,
    size: p.size ?? undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`breakdown failed (${r.status})`);
  return r.json() as Promise<StockBreakdownResponse>;
}
